<template>
  <div class="v2026-si-anchor" @keydown.esc="onEsc">
    <button type="button"
            ref="triggerBtn"
            class="v2026-si-pill"
            :class="{ 'v2026-si-pill--on': open }"
            aria-label="เพิ่ม Icon สถานะ"
            aria-haspopup="dialog"
            :aria-expanded="open ? 'true' : 'false'"
            aria-controls="v2026-si-card"
            @click.prevent="toggleCard">
      <span class="v2026-si-pill__lead" aria-hidden="true"
            :style="{ '-webkit-mask-image': 'url(' + leadIcon + ')', 'mask-image': 'url(' + leadIcon + ')' }"></span>
      <i class="fas fa-chevron-down v2026-si-pill__chev" aria-hidden="true"></i>
    </button>

    <!-- :renderKey is mandatory — the card's contents are state that does NOT flow through props,
         so Vue 2.7's stable-slot optimisation would freeze them. -->
    <OverlayCard id="v2026-si-card"
                 class="v2026-si-pos"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 role="dialog"
                 aria-labelledby="v2026-si-title"
                 @close="close()">
      <template #default="rk">
        <div id="v2026-si-title" class="v2026-si-card__title" :data-rk="rk.dirty">เพิ่ม Icon สถานะ</div>

        <form class="v2026-si-form" :aria-busy="submitting ? 'true' : null" @submit.prevent="onConfirm">
          <div class="v2026-si-list">
            <label v-for="item in catalog"
                   :key="item.id + '#' + (isChecked(item.id) ? 1 : 0)"
                   :for="'v2026-si-opt-' + item.id"
                   class="v2026-si-opt"
                   :class="{ 'v2026-si-opt--on': isChecked(item.id) }">
              <span class="v2026-si-check">
                <input type="checkbox"
                       class="v2026-si-check__input cursor-pointer"
                       :id="'v2026-si-opt-' + item.id"
                       :checked="isChecked(item.id)"
                       :disabled="submitting"
                       @change="toggleDraft(item.id)">
                <span class="v2026-si-check__box" aria-hidden="true"></span>
              </span>
              <!-- js/all.js REPLACES the <i> with an <svg> of its own (and rewrites FA5 alias
                   class names on it), so size and paint the WRAPPER, not the glyph. -->
              <span class="v2026-si-opt__glyph" :style="{ color: item.color }" aria-hidden="true">
                <span v-if="item.badgeText" class="v2026-si-badge">
                  <span class="v2026-si-badge__t">{{ item.badgeText }}</span>
                </span>
                <template v-else><i v-for="n in (item.repeat || 1)" :key="n" :class="item.fa"></i></template>
              </span>
              <!-- lang="th" is what makes the browser use the Thai dictionary for line breaking. -->
              <span class="v2026-si-opt__label" lang="th">{{ item.label }}</span>
            </label>
          </div>

          <!-- Inline, NOT $swal: sweetalert2 mounts at document.body, so clicking its button is an
               outside click that closes this card and destroys the draft. -->
          <p v-if="errorMessage" id="v2026-si-error" class="v2026-si-error" role="alert">{{ errorMessage }}</p>

          <div class="v2026-si-footer">
            <button type="button"
                    class="v2026-si-reset"
                    :disabled="!canReset || submitting"
                    aria-label="ล้างที่เลือก"
                    title="ล้างที่เลือก"
                    @click="resetDraft">
              <span class="v2026-si-reset__ico" aria-hidden="true"><i class="fas fa-rotate-right"></i></span>
            </button>
            <button type="submit"
                    ref="confirmBtn"
                    class="v2026-si-confirm"
                    :disabled="submitting"
                    :aria-describedby="errorMessage ? 'v2026-si-error' : null">ยืนยัน</button>
          </div>
        </form>
      </template>
    </OverlayCard>
  </div>
</template>

<script>
import OverlayCard from '@/components/coversation/OverlayCard'
import { STATUS_ICON_CATALOG, statusIconsForRail } from '@/components/coversation/statusIconCatalog'

export default {
  name: 'status-icon-picker-v2026',
  components: {
    OverlayCard
  },
  props: {
    // Read ONCE, when the card opens, to seed the draft. What the rows render is the draft.
    savedIds: {
      type: Array,
      default: () => []
    },
    // A PRIMITIVE, so the watcher fires only on a real conversation change — the socket echo
    // re-assigns selectedConversation many times a minute for the very same conversation.
    conversationId: {
      type: [Number, String],
      default: null
    },
    submitting: {
      type: Boolean,
      default: false
    },
    // customer_type of the conversation — only icons whose `rails` include it are listed.
    // Saved ids from another rail stay in the draft (hidden), so ยืนยัน never drops them.
    customerType: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      open: false,
      // THE DRAFT. Seeded on open, emitted whole on ยืนยัน, zeroed by close() on every other exit.
      draftIds: [],
      // Cleared by the next tick and by close(), so a stale message cannot outlive its edit.
      errorMessage: null,
      // Handed to OverlayCard as :ignore, so clicking the trigger while open closes it once.
      triggerEl: null,
      leadIcon: require('@/assets/icons2026/chat-favourite.svg')
    }
  },
  computed: {
    catalog () {
      return statusIconsForRail(this.customerType)
    },
    // Signature of EVERYTHING the slot renders. Anything left out of this string freezes on
    // screen behind Vue 2.7's stable-slot optimisation.
    renderKey () {
      return this.draftIds.join('|') + '#' + (this.submitting ? 1 : 0) + '#' + (this.errorMessage || '') + '#' + (this.customerType || '')
    },
    canReset () {
      return this.draftIds.length > 0
    }
  },
  watch: {
    conversationId () {
      this.close()
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    isChecked (id) {
      return this.draftIds.indexOf(id) !== -1
    },
    groupOf (id) {
      const entry = STATUS_ICON_CATALOG.find(item => item.id === id)
      return entry ? entry.group || null : null
    },
    toggleCard () {
      if (this.open) {
        this.close({ returnFocus: true })
        return
      }
      this.open = true
      this.draftIds = (this.savedIds || []).slice()
      this.errorMessage = null
    },
    toggleDraft (id) {
      if (this.submitting) return
      if (this.isChecked(id)) {
        this.draftIds = this.draftIds.filter(item => item !== id)
      } else {
        // Same group = one choice (งานด่วน level): the new pick replaces its siblings.
        const group = this.groupOf(id)
        this.draftIds = this.draftIds
          .filter(item => !group || this.groupOf(item) !== group)
          .concat([id])
      }
      this.errorMessage = null
      this.$nextTick(() => {
        const input = this.$el.querySelector('#v2026-si-opt-' + id)
        if (input && !input.disabled) input.focus({ preventScroll: true })
      })
    },
    // Draft only — nothing is saved until ยืนยัน.
    resetDraft () {
      if (this.submitting) return
      this.draftIds = []
      this.errorMessage = null
    },
    onConfirm () {
      if (this.submitting) return
      this.$emit('confirm', this.draftIds.slice())
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.close({ returnFocus: true })
    },
    close ({ returnFocus = false } = {}) {
      this.open = false
      this.draftIds = []
      this.errorMessage = null
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    showError (message) {
      this.errorMessage = message
      this.$nextTick(() => {
        if (this.$refs.confirmBtn) this.$refs.confirmBtn.focus({ preventScroll: true })
      })
    }
  }
}
</script>

<style scoped>
.v2026-si-anchor {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.v2026-si-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-1, 4px);
  height: var(--v2026-ctrl-h, 36px);
  padding: 0 var(--v2026-sp-2, 8px);
  border: 1px solid var(--v2026-pill-border, #EBEDF0);
  border-radius: 4px;
  background: var(--v2026-surface, #FFFFFF);
  color: var(--v2026-text-secondary, #667085);
  font-size: var(--v2026-fs-ctrl, 13px);
  flex-shrink: 0;
  transition: background-color 150ms ease;
}

.v2026-si-pill:hover,
.v2026-si-pill--on {
  background: var(--v2026-surface-hover, #E9ECF1);
}

.v2026-si-pill__lead {
  width: 16px;
  height: 16px;
  display: inline-block;
  flex-shrink: 0;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon, #475467);
}

.v2026-si-pill__chev {
  font-size: 10px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-si-pos {
  top: calc(100% + var(--v2026-sp-2, 8px));
  left: 0;
  width: 320px;
  max-width: calc(100vw - 24px);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 180px);
}

.v2026-si-form {
  margin: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.v2026-si-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;
}

.v2026-si-reset {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #ffffff;
  color: #475467;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-si-reset:hover:not(:disabled) {
  background: #f8fafc;
  color: #1f2937;
}

.v2026-si-reset:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.v2026-si-reset:focus-visible {
  outline: 2px solid var(--v2026-color-main, #31C1D7);
  outline-offset: 2px;
}

.v2026-si-reset__ico {
  display: inline-flex;
  font-size: 14px;
  line-height: 1;
}

.v2026-si-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.v2026-si-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  margin-bottom: 0;
  padding: 9px 14px;
  border: 1px solid #E5E7EB;
  border-radius: 999px;
  background: #FFFFFF;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
}

.v2026-si-opt:hover {
  border-color: var(--v2026-color-main, #31C1D7);
}

.v2026-si-opt--on {
  border-color: var(--v2026-color-main, #31C1D7);
  box-shadow: inset 0 0 0 1px var(--v2026-color-main, #31C1D7);
  background: var(--v2026-color-main-soft, #31C1D714);
}

.v2026-si-error {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: #F04438;
  overflow-wrap: break-word;
}

.v2026-si-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--v2026-sp-2, 8px);
  margin-top: var(--v2026-sp-4, 16px);
}

.v2026-si-confirm {
  height: 40px;
  padding: 0 var(--v2026-sp-5, 20px);
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main, #31C1D7);
  color: #fff;
  font-family: inherit;
  font-size: var(--v2026-fs-body, 14px);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-si-confirm:hover:not(:disabled) {
  background: var(--v2026-color-main-hover, #2CAEC2);
}

.v2026-si-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.v2026-si-confirm:focus-visible {
  outline: 2px solid var(--v2026-color-main, #31C1D7);
  outline-offset: 2px;
}

.v2026-si-check {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  width: 18px;
  height: 18px;
  line-height: 1;
}

.v2026-si-check__input {
  position: absolute;
  top: 0;
  left: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  opacity: 0;
}

.v2026-si-check__box {
  position: relative;
  width: 18px;
  height: 18px;
  border: 1.5px solid var(--v2026-color-main, #31C1D7);
  border-radius: 6px;
  background: #FFFFFF;
  pointer-events: none;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.v2026-si-check__box::after {
  content: '';
  position: absolute;
  top: 1px;
  left: 4.5px;
  width: 5px;
  height: 9px;
  border: solid var(--v2026-color-main, #31C1D7);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 150ms ease;
}

.v2026-si-check__input:checked + .v2026-si-check__box {
  background: var(--v2026-color-main-soft, #31C1D714);
}

.v2026-si-check__input:checked + .v2026-si-check__box::after {
  opacity: 1;
}

.v2026-si-check__input:focus-visible + .v2026-si-check__box {
  outline: 2px solid var(--v2026-color-main, #31C1D7);
  outline-offset: 2px;
}

.v2026-si-opt__glyph {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  min-width: 18px;
  font-size: 16px;
  line-height: 1;
}

.v2026-si-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 13px;
  padding: 0 3px;
  border-radius: 3px;
  background: currentColor;
}

.v2026-si-badge__t {
  color: #ffffff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
}

.v2026-si-opt__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 20px;
  overflow-wrap: break-word;
}
</style>
